// `prompt_composed` — a `status` sub-event a seat emits once per distinct system prompt,
// where the FINAL text is built (plan 101 slice 01). Carries the prompt audit (names,
// UTF-8 bytes, sha256 per layer and of the whole) — never the text.
// Split into its own module under the `../task-run-event.ts` barrel.
//
// THE ARTIFACT MAP RIDES BESIDE THE SET ID (arc 101 Part C). `prompt_version_set_id`
// is ONE value over the WHOLE set: it groups sessions and answers "same prompts?",
// nothing else. `artifact_versions` is the build's WHOLE artifact map — one 12-hex digest
// per server-authored prompt artifact — so a reader can say WHICH prompt file moved between
// two groups, which is the question a regression has to be answered with. It is NOT the set
// id broken down: a box sends EVERY `PROMPT_VERSIONS` entry
// (`apps/runner/src/prompt-audit.ts`), while the stored id is the CODING lane's, whose input
// excludes `screen` — so the map is one artifact WIDER than the id, and a `screen.md` edit
// moves the map without moving the id.
// OPTIONAL, and absent means "this build sent no map": a box older than the field
// parses and persists a row without it, and that row still groups by set id.

import { z } from 'zod';
import { AgentModeEchoSchema } from '../../entities/agent-mode';
import { PromptAuditSchema } from '../prompt-audit';

/**
 * The prompt artifacts this repository ships under `packages/agent/src/prompts/`, plus
 * the static prose constants a run reads — the closed set `PROMPT_VERSIONS`
 * (`@developerz/agent`, `prompts/versions.ts`) is keyed by. MIRRORED, never imported:
 * `@developerz/domain` imports nothing internal, and `packages/agent` imports this one.
 * The key is a stored value, so renaming one is a rename of stored rows; a key outside
 * this set is REFUSED rather than stored.
 */
export const PROMPT_ARTIFACTS = [
  'platform',
  'loop-issue',
  'loop-pr',
  'loop-release',
  'checkup',
  'screen',
  'role-devops',
  'role-janitor',
  'role-live-check',
  'role-plan',
  'role-review',
  'role-scout',
  'role-triage',
  'role-work',
  'text-expect',
  'text-contract',
  'text-scope',
  'lead-brief',
  'brief-triage-call',
  'brief-plan-slice',
  'brief-plan-sequencing',
  'guidance-codegraph',
  'rules-compaction-summary',
  'instruction-classifier-reason',
  'user-default',
] as const;

/** One artifact's stable name (`platform`, `role-work`, `loop-pr`, …). */
export type PromptArtifact = (typeof PROMPT_ARTIFACTS)[number];

/**
 * Artifact name → its version: a sha256 digest truncated to 12 hex chars, the width
 * `@developerz/agent` mints (`DIGEST_CHARS`). Platform-minted, so the SHAPE is the
 * fence — a value that is not a digest is refused rather than stored.
 *
 * `partialRecord`, not `record`: zod v4's `record` requires EVERY key of the enum to be
 * present, which would refuse a subset — and a subset is the normal case, a set built
 * before an artifact was added. A partialRecord admits the subset and still REFUSES a
 * key outside the closed set, which is the half that matters: an artifact name is a
 * stored value, so an invented one must never reach a row. Empty is refused too — an
 * empty map names no artifact and says nothing about which prompt moved.
 */
export const PromptArtifactVersionsSchema = z
  .partialRecord(z.enum(PROMPT_ARTIFACTS), z.string().regex(/^[0-9a-f]{12}$/))
  .refine((map) => Object.keys(map).length > 0, {
    message: 'artifact_versions names no prompt artifact',
  });
export type PromptArtifactVersions = z.infer<typeof PromptArtifactVersionsSchema>;

export const PROMPT_COMPOSED_EVENT = 'prompt_composed';

/** The closed set of seats that compose a system prompt. */
export const PROMPT_SEATS = [
  'coder',
  'coordinator',
  'editor',
  'scout',
  'scout_survey',
  'reviewer',
  'review_planner',
  /** The debugger lane's read-only PROBES (and their lead) — grounding passes, never editors. */
  'devops_probe',
  /** The onboarding (setup) lane's native harness run. */
  'onboarding',
  /** A `pr_review_reply` thread answer (`apps/runner/src/modes/worker/review-threads.ts`). */
  'review_reply',
  /** The review lane's re-check of its own open threads (`review-brain/recheck-judge.ts`). */
  'review_recheck',
  /** The strict second review of an external PR (`review-brain/strict-judge.ts`). */
  'review_strict',
  /**
   * The review lane's verifier judge (`review-brain/verifier-judge.ts`, 12-7): the platform
   * floor plus `VERIFIER_SYSTEM`. Declared before any box records it, so the mothership
   * parses the seat the day a box starts sending it.
   */
  'verifier',
  /**
   * The model-call lane's one structured call (#6760, `apps/runner/src/modes/worker/model-call/`):
   * the system prompt the cluster caller composed, served to the box verbatim. Declared in
   * the same PR as its first box writer: the mothership rolls first, so it parses the seat
   * before any box sends it.
   */
  'model_call',
] as const;

/**
 * The stored row. The audit is NESTED under `prompt`, exactly as every other row that
 * carries one (`session.boot`), so a reader has one path to it — never flattened.
 *
 * Store BOTH (arc 101 Part C): the set id groups sessions, the artifact map names which
 * prompt file moved between two groups. The id alone answers "same prompts?" and
 * nothing else, so a row written before the map existed still parses and still groups.
 */
export const PromptComposedPayloadSchema = z
  .object({
    event: z.literal(PROMPT_COMPOSED_EVENT),
    seat: z.enum(PROMPT_SEATS),
    /** Which hive seat, when a role runs more than one (e.g. editor 0..n). */
    seat_index: z.number().int().nonnegative().optional(),
    prompt: PromptAuditSchema.extend({
      /** The per-artifact digests, NESTED beside the set id they break down. */
      artifact_versions: PromptArtifactVersionsSchema.optional(),
    }),
    /**
     * The agent mode the box APPLIED to this seat (plan 101 slice 06) — key, the
     * assignment's revision, and the `code.*` tools mounted after narrowing. Absent on
     * an older box, or on a run whose assignment carried no `agent_mode`.
     */
    agent_mode: AgentModeEchoSchema.optional(),
  })
  .strict();
export type PromptComposedPayload = z.infer<typeof PromptComposedPayloadSchema>;
